import React, { useCallback, useEffect, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useFetch } from 'use-http';
import BlockUi from 'react-block-ui';
import { Link, Panel, Spinner, SpinnerSize } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useBoolean } from '@fluentui/react-hooks';

import { DealDetails, bytesForHuman, isNetflixDeal, isDealDeleted } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { AudienceMeter } from '../../steps/ad-group/reach-estimate/audience-meter';
import { getDealPotentialImpression } from './deal-potential-impression-styles';
import { unitValueMap } from '../../steps/ad-group/reach-estimate/utils';

export const DealImpressionPanel = (props) => {
  const {
    deal, i18n, isPanelMode, targets, serverContext, impression,
  } = props;

  const classes = useDefaultStyles(getDealPotentialImpression);
  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [estimateLoaded, setEstimateLoaded] = useState(!!impression);
  const [estImpression, setEstImpression] = useState(impression);

  const dealDeleted = isDealDeleted(deal);
  // hide impression for netflix
  const hideDealImpression = isNetflixDeal(deal) || dealDeleted;

  const {
    DynamicPermissions: {
      GenreTargets,
    },
    Permissions: {
      IsDealFilterEnabled,
    },
  } = serverContext;
  const shouldShowGenre = GenreTargets && IsDealFilterEnabled && deal;

  const {
    post: fetchReachEstimation,
  } = useFetch(`Customers(${serverContext.CurrentCustomer.Id})/Accounts(${serverContext.CurrentAccount.Id})/planner/AudienceEstimation`);

  const debouncedUpdateEstimate = useCallback(_.debounce((data) => {
    fetchReachEstimation('', data).then((res) => {
      if (!res) {
        return;
      }
      const estImpressionsVal = res.estImpression.value;
      const estImpressionsUnitVal = unitValueMap[res.estImpression.unit].value;
      const estImpressions = estImpressionsVal * estImpressionsUnitVal;

      // set to undefine if 0
      if (estImpressions === 0) {
        setEstImpression(undefined);
      } else {
        setEstImpression(estImpressions);
      }
      setEstimateLoaded(true);
    });
  }, 3000), []);

  useEffect(() => {
    if (!impression && !hideDealImpression) {
      setEstimateLoaded(false);
      debouncedUpdateEstimate({
        ...targets,
        DealIds: { Includes: [deal.Id] },
      });
    }
  }, [targets, debouncedUpdateEstimate, deal.Id, impression]);


  return (
    <div className={classnames(classes.dealImpression, isPanelMode && 'panel-mode', 'deal-impression-container')} >
      {dealDeleted ?
        <div className="deal-warning" >
          {i18n.getString(_TL_('Media deal'))}: <span className="deleted-text">{i18n.getString(_TL_('Deleted'))}</span>
        </div>
        : <div className="deal-seller">{i18n.getString(_TL_('Media deal'))}: {deal.Name}</div>
      }
      { estImpression && <div className="desc" >{i18n.getString(_TL_('Potential impression for this deal'))}</div>}
      { estImpression && <div className="impression" >{bytesForHuman(estImpression)}</div> }

      {
        !hideDealImpression &&
        <div className="deal-impression" >
          <BlockUi className="react-blocker" blocking={!estimateLoaded} loader={<Spinner size={SpinnerSize.large} />} >
            <AudienceMeter isDealEstimate estimatedImpressions={estImpression} i18n={i18n} />
          </BlockUi>
        </div>
      }
      {
        !dealDeleted &&
        (
          isPanelMode ?
            <DealDetails dealDetails={deal} i18n={i18n} style={{ padding: '10px 0' }} shouldShowGenre={shouldShowGenre} /> :
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link onClick={openPanel} >{i18n.getString(_TL_('View deal'))}</Link>
        )
      }
      {
        !isPanelMode &&
        <Panel
          styles={{ main: { width: '450px !important' } }}
          isOpen={isOpen}
          isLightDismiss
          onDismiss={dismissPanel}
          closeButtonAriaLabel="Close"
        >
          <DealImpressionPanel impression={estImpression} deal={deal} i18n={i18n} isPanelMode targets={targets} serverContext={serverContext} />
        </Panel>
      }
    </div>
  );
};


DealImpressionPanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  impression: PropTypes.number,
  deal: PropTypes.shape({
    Seller: PropTypes.string,
    Name: PropTypes.string,
    AskPrice: PropTypes.number,
    EstimatedImpressions: PropTypes.number,
    Id: PropTypes.number,
  }).isRequired,
  isPanelMode: PropTypes.bool,
  serverContext: PropTypes.shape({
    CurrentCustomer: PropTypes.shape({ Id: PropTypes.string }),
    CurrentAccount: PropTypes.shape({ Id: PropTypes.string }),
    Permissions: PropTypes.shape({
      IsDealFilterEnabled: PropTypes.bool,
    }),
    DynamicPermissions: PropTypes.shape({
      GenreTargets: PropTypes.bool,
    }),
  }).isRequired,
  targets: PropTypes.shape({}),
};

DealImpressionPanel.defaultProps = {
  isPanelMode: false,
  impression: null,
  targets: {},
};
